import { API_CONFIG, API_ENDPOINTS, STORAGE_KEYS } from '../helpers/constants';
import {
  type ChannelAutomationSettings,
  type LabelAutomationSetting,
  type LabelChannelSettings,
  type SensitivityValue,
  DEFAULT_CHANNEL_AUTOMATION_SETTINGS,
  CHANNEL_AUTOMATION_INTERVAL_OPTIONS,
  type ChannelAutomationIntervalMinutes,
  SENSITIVITY_OPTIONS,
} from '../components/ChannelSettings/automationTabConstants';

/** Must never be sent on save — backend preserves via JSONB merge (UI-05) */
export const FORBIDDEN_AUTOMATION_SAVE_KEYS = ['auto_draft_last_run_at'] as const;

function getBearerToken(): string {
  return (
    localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) ||
    localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) ||
    ''
  );
}

function normalizeIntervalMinutes(value: unknown): ChannelAutomationIntervalMinutes {
  const num = typeof value === 'number' ? value : Number(value);
  if (CHANNEL_AUTOMATION_INTERVAL_OPTIONS.includes(num as ChannelAutomationIntervalMinutes)) {
    return num as ChannelAutomationIntervalMinutes;
  }
  return DEFAULT_CHANNEL_AUTOMATION_SETTINGS.auto_draft_interval_minutes as ChannelAutomationIntervalMinutes;
}

function normalizeSensitivity(value: unknown): SensitivityValue | undefined {
  if (typeof value !== 'string') return undefined;
  const valid = SENSITIVITY_OPTIONS.some((o) => o.value === value);
  return valid ? (value as SensitivityValue) : undefined;
}

/** True when channel_settings.labels is absent or has no keys (UI-04 first-time banner) */
export function isChannelLabelsEmpty(labels: Record<string, LabelChannelSettings> | undefined): boolean {
  return !labels || Object.keys(labels).length === 0;
}

function parseLabelsMap(raw: unknown): Record<string, LabelChannelSettings> {
  if (!raw || typeof raw !== 'object') return {};
  const result: Record<string, LabelChannelSettings> = {};
  for (const [name, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!value || typeof value !== 'object') continue;
    const entry = value as Record<string, unknown>;
    result[name] = {
      auto_draft_enabled: entry.auto_draft_enabled === true,
      ...(typeof entry.auto_reply_enabled === 'boolean'
        ? { auto_reply_enabled: entry.auto_reply_enabled }
        : {}),
      ...(normalizeSensitivity(entry.auto_reply_sensitivity)
        ? { auto_reply_sensitivity: normalizeSensitivity(entry.auto_reply_sensitivity) }
        : {}),
    };
  }
  return result;
}

/** Build table rows: one per subtag; absent channel_settings.labels key => OFF */
export function buildLabelRows(
  subtags: string[],
  labelsFromApi: Record<string, LabelChannelSettings> | undefined
): LabelAutomationSetting[] {
  const safeSubtags = Array.isArray(subtags) ? subtags : [];
  const labels = labelsFromApi ?? {};
  return safeSubtags.map((name) => {
    const stored = labels[name];
    if (!stored) {
      return { name, auto_draft_enabled: false };
    }
    return {
      name,
      auto_draft_enabled: stored.auto_draft_enabled === true,
      auto_reply_enabled: stored.auto_reply_enabled,
      auto_reply_sensitivity: stored.auto_reply_sensitivity,
    };
  });
}

/** Convert UI rows to channel_settings.labels for API persistence (full object, not delta) */
export function labelRowsToSettings(
  rows: LabelAutomationSetting[]
): Record<string, LabelChannelSettings> {
  const labels: Record<string, LabelChannelSettings> = {};
  for (const row of rows) {
    const entry: LabelChannelSettings = {
      auto_draft_enabled: row.auto_draft_enabled,
    };
    if (row.auto_draft_enabled) {
      if (typeof row.auto_reply_enabled === 'boolean') {
        entry.auto_reply_enabled = row.auto_reply_enabled;
      }
      if (row.auto_reply_enabled && row.auto_reply_sensitivity) {
        entry.auto_reply_sensitivity = row.auto_reply_sensitivity;
      } else if (row.auto_reply_enabled) {
        entry.auto_reply_sensitivity = 'balanced';
      }
    }
    labels[row.name] = entry;
  }
  return labels;
}

export interface ChannelAutomationData {
  settings: ChannelAutomationSettings;
  labels: Record<string, LabelChannelSettings>;
  subtags: string[];
  labelRows: LabelAutomationSetting[];
}

export interface AutomationSnapshot {
  settings: ChannelAutomationSettings;
  labelRows: LabelAutomationSetting[];
}

export function createAutomationSnapshot(
  settings: ChannelAutomationSettings,
  labelRows: LabelAutomationSetting[]
): AutomationSnapshot {
  return {
    settings: { ...settings },
    labelRows: labelRows.map((row) => ({ ...row })),
  };
}

export function isAutomationStateDirty(
  baseline: AutomationSnapshot,
  settings: ChannelAutomationSettings,
  labelRows: LabelAutomationSetting[]
): boolean {
  if (baseline.settings.auto_draft_enabled !== settings.auto_draft_enabled) return true;
  if (baseline.settings.auto_draft_interval_minutes !== settings.auto_draft_interval_minutes) {
    return true;
  }
  if (baseline.labelRows.length !== labelRows.length) return true;

  const baselineByName = new Map(baseline.labelRows.map((r) => [r.name, r]));

  return labelRows.some((row) => {
    const base = baselineByName.get(row.name);
    if (!base) return true;
    if (base.auto_draft_enabled !== row.auto_draft_enabled) return true;
    if (base.auto_reply_enabled !== row.auto_reply_enabled) return true;
    if (base.auto_reply_sensitivity !== row.auto_reply_sensitivity) return true;
    return false;
  });
}

export interface SaveChannelAutomationPayload {
  settings: ChannelAutomationSettings;
  labelRows: LabelAutomationSetting[];
}

/** Matches backend ChannelUpdate schema — all fields optional; automation sends channel_settings only */
export interface ChannelUpdateRequest {
  name?: string;
  description?: string;
  is_public?: boolean;
  is_active?: boolean;
  is_archived?: boolean;
  ai_enabled?: boolean;
  channel_settings?: {
    auto_draft_enabled: boolean;
    auto_draft_interval_minutes: number;
    labels: Record<string, LabelChannelSettings>;
  };
  channel_tags?: string[];
  channel_sub_tags?: string[];
  channel_business_rules?: string;
}

/** Channel update API response (GET/PUT /api/v1/channels/{channel_id}) */
export interface ChannelUpdateResponse {
  id: string;
  name: string;
  description?: string;
  channel_tags?: string[];
  channel_sub_tags?: string[];
  workspace_name?: string;
  workspace_id?: string;
  company_id?: string;
  is_active?: boolean;
  is_public?: boolean;
  is_archived?: boolean;
  ai_enabled?: boolean;
  channel_settings?: Record<string, unknown>;
  channel_business_rules?: string;
  updated_at?: string;
  [key: string]: unknown;
}

/** Build PUT body for ChannelUpdate — only channel_settings; never includes auto_draft_last_run_at */
export function buildChannelUpdatePayload(payload: SaveChannelAutomationPayload): ChannelUpdateRequest {
  const labels = labelRowsToSettings(payload.labelRows);
  const channel_settings: ChannelUpdateRequest['channel_settings'] = {
    auto_draft_enabled: payload.settings.auto_draft_enabled,
    auto_draft_interval_minutes: payload.settings.auto_draft_interval_minutes,
    labels,
  };

  for (const forbidden of FORBIDDEN_AUTOMATION_SAVE_KEYS) {
    if (forbidden in channel_settings) {
      delete (channel_settings as Record<string, unknown>)[forbidden];
    }
  }

  return { channel_settings };
}

/** @deprecated Use buildChannelUpdatePayload */
export function buildChannelSettingsSaveBody(payload: SaveChannelAutomationPayload) {
  return buildChannelUpdatePayload(payload);
}

/** Parse channel_settings automation fields from a channel API response */
export function parseChannelAutomationSettings(channel: unknown): ChannelAutomationSettings {
  const record = channel as { channel_settings?: Record<string, unknown> } | null | undefined;
  const cs = record?.channel_settings ?? {};

  return {
    auto_draft_enabled:
      typeof cs.auto_draft_enabled === 'boolean'
        ? cs.auto_draft_enabled
        : DEFAULT_CHANNEL_AUTOMATION_SETTINGS.auto_draft_enabled,
    auto_draft_interval_minutes: normalizeIntervalMinutes(cs.auto_draft_interval_minutes),
  };
}

export function parseChannelAutomationData(
  channel: unknown,
  subtagsOverride?: string[]
): ChannelAutomationData {
  const record = channel as {
    channel_settings?: Record<string, unknown>;
    channel_sub_tags?: string[];
  } | null | undefined;

  const settings = parseChannelAutomationSettings(channel);
  const labels = parseLabelsMap(record?.channel_settings?.labels);
  const subtags =
    subtagsOverride ??
    (Array.isArray(record?.channel_sub_tags) ? record.channel_sub_tags : []);

  return {
    settings,
    labels,
    subtags,
    labelRows: buildLabelRows(subtags, labels),
  };
}

/** Load channel automation settings and label rows from GET /channels/:id */
export async function fetchChannelAutomationData(
  channelId: string,
  subtagsOverride?: string[]
): Promise<ChannelAutomationData> {
  const token = getBearerToken();
  const response = await fetch(
    `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHANNELS.GET_BY_ID(channelId)}`,
    {
      method: 'GET',
      headers: {
        accept: 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    }
  );

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    const detail = typeof err.detail === 'string' ? err.detail : undefined;
    throw new Error(detail || `Failed to load automation settings (${response.status})`);
  }

  const data = await response.json();
  return parseChannelAutomationData(data, subtagsOverride);
}

/** @deprecated Use fetchChannelAutomationData */
export async function fetchChannelAutomationSettings(
  channelId: string
): Promise<ChannelAutomationSettings> {
  const data = await fetchChannelAutomationData(channelId);
  return data.settings;
}

/**
 * Persist automation settings via PUT /api/v1/channels/{channel_id}
 * Sends ChannelUpdate with channel_settings only (partial update).
 */
export async function saveChannelAutomationSettings(
  channelId: string,
  payload: SaveChannelAutomationPayload
): Promise<ChannelUpdateResponse> {
  const token = getBearerToken();
  if (!token) {
    throw new Error('Authentication required. Please sign in again.');
  }

  const body = buildChannelUpdatePayload(payload);
  const serialized = JSON.stringify(body);
  for (const forbidden of FORBIDDEN_AUTOMATION_SAVE_KEYS) {
    if (serialized.includes(forbidden)) {
      throw new Error(`Save payload must not include ${forbidden}`);
    }
  }

  const response = await fetch(
    `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHANNELS.UPDATE(channelId)}`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: serialized,
    }
  );

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    const detail = typeof err.detail === 'string' ? err.detail : undefined;
    throw new Error(detail || `Failed to save automation settings (${response.status})`);
  }

  return (await response.json()) as ChannelUpdateResponse;
}
